import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod/v4";

import { api } from "@/lib/api-client";
import { getGarageCarsQueryOptions } from "@/services/cars/get-garage-cars";

import type { MutationConfig } from "@/lib/react-query";
import type { Car } from "@/types/car";

export const newCarSchema = z.object({
  nickname: z.string().optional(),
  owner: z.string().nonempty(),
  purchase_price: z.number().positive("Price must be greater than 0"),
  purchase_date: z.string().nonempty(),
  production_year: z
    .number()
    .min(1900, "Year must be at least 1900")
    .max(new Date().getFullYear(), `Year cannot be in the future`),
  make: z.string().nonempty(),
  family: z.string().nonempty(),
  generation: z.string().nonempty(),
  model: z.string().nonempty(),
  variant: z.string().nonempty(),
  licence_plate: z.string().optional(),
  country_of_registration: z.string().optional(),
  color: z.string(),
  interior_colors: z.string(),
  condition: z.enum(["superb", "excellent", "good", "fair", ""]).optional(),
  number_of_previous_owner: z.number().optional(),
  status: z.enum(["owned", "totaled", "sold", "traded"]).default("owned"),
  special_equipment: z.string().optional(),
  vin: z.string().optional(),
});

export type CreateCarInput = z.infer<typeof newCarSchema>;

export const createCar = async ({
  data,
  garageId,
}: {
  data: CreateCarInput;
  garageId?: string;
}): Promise<Car> => {
  const params = garageId ? { garage_id: garageId } : undefined;
  return (await api.post("/carburate/cars", data, { params })) as Car;
};

type UseCreateCarOptions = {
  mutationConfig?: MutationConfig<typeof createCar>;
};

export const useCreateCar = ({ mutationConfig }: UseCreateCarOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, vars, ...args) => {
      if (vars.garageId) {
        queryClient.invalidateQueries({ queryKey: ["garages", vars.garageId] });
        queryClient.invalidateQueries({ queryKey: ["garages"] });
        queryClient.invalidateQueries({
          queryKey: getGarageCarsQueryOptions({
            garageId: vars.garageId,
            page: 1,
            pageSize: 999999,
          }).queryKey,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["garage-market-car-ids"] });
      // Ensure portfolio totals are refreshed when cars change
      queryClient.invalidateQueries({ queryKey: ["portfolio"] });
      onSuccess?.(data, vars, ...args);
    },
    ...restConfig,
    mutationFn: createCar,
  });
};
